import { keyBy } from 'lodash';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';
import { ProjectId } from 'api/models';
import { TaskAPI } from 'api/tasks';
import { RootState } from 'data';
import { Task as NormalizedTask, taskNormalizer } from 'data/tasks/schema';
import { useProjectInvitationHandler } from 'hooks/use-invite-user';
import { useUser } from 'hooks/use-user';
import { logger } from 'services';
import { FieldsStore } from 'stores/fields';
import { created, creating, failed } from 'stores/tasks';
import { callWorkerWithCleanup } from 'utils/call-worker-with-cleanup';
import { withValidCustomFieldsForData } from 'utils/duplicate-fields-transformer';
import { queueMacrotask } from 'utils/macrotask';
import { toStoreTask } from 'utils/to-store-task';
import {
  getAddedUsersFromUpdates,
  PartialValue,
  UPDATES_TRANSFORMER,
} from './use-update-tasks';

type CreateTask = (expectations: PartialValue[]) => void;

const useFieldLookup = () => {
  const fieldsStore = useSelector(({ fields }: RootState) => fields);

  return keyBy(FieldsStore.enabledTaskFields(fieldsStore), (f) => f.fieldId);
};

export const useCreateTask = (): CreateTask => {
  const dispatch = useDispatch();
  const fields = useFieldLookup();
  const handleExpectations = useProjectInvitationHandler(
    UPDATES_TRANSFORMER,
    getAddedUsersFromUpdates,
  );
  const {
    user: { userId: createdBy },
  } = useUser();

  const handleProjectExpectations = useCallback(
    async (
      projectId: ProjectId,
      expectations: PartialValue[],
    ): Promise<PartialValue[]> => {
      const worker = callWorkerWithCleanup();
      const notInProjectUsers = await worker.workerApi
        .getUsersNotInProject(projectId[1])
        .finally(() => {
          worker.cleanup();
        });

      return handleExpectations(expectations, projectId, notInProjectUsers);
    },
    [handleExpectations],
  );

  return useCallback(
    (expectations: PartialValue[]) =>
      queueMacrotask(async () => {
        const project = expectations.find(
          (x) => x.field.fieldName === 'project',
        )?.to as number | undefined;

        if (project == null) {
          logger.error('project_field_missing', {
            error: new Error(
              'project field information was not passed to create method',
            ),
          });

          return;
        }
        const project_id = ProjectId(project);
        const handled_expectations = await handleProjectExpectations(
          project_id,
          expectations,
        );
        const data = PartialValue.toPatch(handled_expectations);
        const request_id = uuid();
        const normalized_task = {
          ...data,
          project,
          createdBy,
          subTasks: [],
          dependentTasks: [],
        } as unknown as NormalizedTask;
        const next_task = toStoreTask(fields, {})(normalized_task);

        dispatch(creating([next_task, request_id]));
        try {
          const result = await TaskAPI.createTaskWithRequestId(
            project_id,
            data,
            request_id,
          );
          const updatedTask = {
            ...result,
            fields: result.fields.map(withValidCustomFieldsForData),
          };
          const {
            entities: { tasks },
          } = taskNormalizer(updatedTask);

          Object.values(tasks).forEach((normalized) => {
            const newTask = toStoreTask(fields, {})(normalized);

            dispatch(created([newTask, request_id]));
          });
        } catch (error) {
          dispatch(failed(request_id));
          logger.error('create task failed', { error });
        }
      }),
    [dispatch, handleProjectExpectations, fields, createdBy],
  );
};
